import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { admissionsApi } from '@/services/api/endpoints/admissions';
import { sectionsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  AdmissionApplication,
  AdmissionDocument,
  ApplicationStatus,
} from '@/types/admission';
import { formatDate, formatDateTime, humanise, orDash } from '@/utils/format';
import { ApplicationFormModal } from './AdmissionsPage';

/** Suggested labels only — the server stores whatever type it is given. */
const DOCUMENT_TYPES = [
  'BIRTH_CERTIFICATE',
  'PHOTOGRAPH',
  'PREVIOUS_SCHOOL_RECORD',
  'TRANSFER_CERTIFICATE',
  'MEDICAL_RECORD',
  'IDENTIFICATION',
  'OTHER',
];

type Move = 'review' | 'schedule' | 'record' | 'offer' | 'accept' | 'reject' | 'withdraw' | 'enrol';

/*
 * Which moves to offer from each status. This only decides which buttons are
 * worth showing — the server validates every transition and its refusal is
 * shown verbatim, so a stale page cannot push an application somewhere invalid.
 */
const MOVES: Record<ApplicationStatus, Move[]> = {
  SUBMITTED: ['review', 'schedule', 'reject', 'withdraw'],
  UNDER_REVIEW: ['schedule', 'offer', 'reject', 'withdraw'],
  ASSESSMENT_SCHEDULED: ['record', 'schedule', 'offer', 'reject', 'withdraw'],
  OFFERED: ['accept', 'reject', 'withdraw'],
  ACCEPTED: ['enrol', 'withdraw'],
  ENROLLED: [],
  REJECTED: [],
  WITHDRAWN: [],
};

/** The form can be corrected until a decision is made. */
const EDITABLE: ApplicationStatus[] = ['SUBMITTED', 'UNDER_REVIEW', 'ASSESSMENT_SCHEDULED'];

/** Offering, rejecting and enrolling are decisions; everything else is handling. */
const DECISIONS: Move[] = ['offer', 'reject', 'enrol'];

const MOVE_LABEL: Record<Move, string> = {
  review: 'Start review',
  schedule: 'Schedule assessment',
  record: 'Record assessment',
  offer: 'Offer a place',
  accept: 'Record acceptance',
  reject: 'Reject',
  withdraw: 'Withdraw',
  enrol: 'Enrol',
};

/** Bytes, in the units a person reads. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function optional(value: string): string | undefined {
  return value.trim() === '' ? undefined : value.trim();
}

/**
 * One application: the form as received, where it stands, and the moves open
 * from here.
 *
 * <p>Every move is its own request and the server decides whether it is legal
 * from the current status. Enrolment creates the student, the enrolment and
 * any admission-fee invoice together, and links back to the new student.
 */
export function AdmissionDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<Move | null>(null);

  const application = useQuery({
    queryKey: queryKeys.admissions.detail(id),
    queryFn: () => admissionsApi.get(id),
    enabled: id !== '',
  });

  if (application.isPending) {
    return <LoadingState label="Loading application" />;
  }
  if (application.error !== null) {
    return <ErrorState error={application.error} onRetry={() => void application.refetch()} />;
  }

  const record = application.data;
  const moves = MOVES[record.status].filter((move) =>
    can(DECISIONS.includes(move) ? PERMISSIONS.ADMISSION_DECIDE : PERMISSIONS.ADMISSION_MANAGE),
  );
  const editable = can(PERMISSIONS.ADMISSION_MANAGE) && EDITABLE.includes(record.status);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.admissions.all });

  return (
    <>
      <Link to={ROUTES.admissions} className="mb-3 inline-block text-sm text-muted hover:text-ink">
        ← Back to admissions
      </Link>

      <PageHeader
        title={record.applicantName}
        description={`Application ${record.applicationNumber}`}
        action={
          <div className="flex flex-wrap gap-2">
            {moves.map((move) => (
              <Button
                key={move}
                variant={move === 'reject' || move === 'withdraw' ? 'ghost' : 'secondary'}
                onClick={() => setPending(move)}
              >
                {MOVE_LABEL[move]}
              </Button>
            ))}
            {editable && <Button onClick={() => setEditing(true)}>Edit</Button>}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Application" />
          <dl className="grid gap-x-8 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <Detail label="Status" value={<StatusBadge status={record.status} />} />
            <Detail label="Received" value={formatDate(record.createdAt)} />
            <Detail label="Academic year" value={orDash(record.academicYearName)} />
            <Detail label="Grade" value={orDash(record.gradeName)} />
            <Detail label="Date of birth" value={formatDate(record.dateOfBirth)} />
            <Detail label="Gender" value={record.gender === undefined ? '—' : humanise(record.gender)} />
            <Detail label="Previous school" value={orDash(record.previousSchool)} />
            <Detail label="Notes" value={orDash(record.notes)} />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Guardian" />
          <dl className="space-y-4 px-5 py-4">
            <Detail label="Name" value={record.guardianName} />
            <Detail
              label="Relationship"
              value={record.guardianRelationship === undefined ? '—' : humanise(record.guardianRelationship)}
            />
            <Detail label="Phone" value={record.guardianPhone} />
            <Detail label="Email" value={orDash(record.guardianEmail)} />
          </dl>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Assessment and decision" />
          <dl className="grid gap-x-8 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <Detail label="Assessment" value={formatDateTime(record.assessmentAt)} />
            <Detail label="Assessment notes" value={orDash(record.assessmentNotes)} />
            <Detail label="Decided" value={formatDateTime(record.decidedAt)} />
            <Detail label="Decision note" value={orDash(record.decisionReason)} />
            {record.enrolledStudentId !== undefined && (
              <Detail
                label="Student"
                value={
                  <Link
                    to={ROUTES.studentDetail(record.enrolledStudentId)}
                    className="text-accent underline-offset-2 hover:underline"
                  >
                    Open student record
                  </Link>
                }
              />
            )}
          </dl>
        </Card>

        <AdmissionDocumentsPanel applicationId={id} />
      </div>

      <ApplicationFormModal
        open={editing}
        application={record}
        onClose={() => setEditing(false)}
        onSaved={() => {
          toast.success('Application updated');
          setEditing(false);
          refresh();
        }}
        onError={(error) => handleApiError(error)}
      />

      {pending !== null && (
        <MoveDialog
          move={pending}
          application={record}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

/**
 * The dialog for one move. Moves without input are a plain confirmation; the
 * rest ask for exactly what the endpoint takes, and require it where the
 * endpoint refuses a blank value.
 */
function MoveDialog({
  move,
  application,
  onClose,
  onDone,
}: {
  move: Move;
  application: AdmissionApplication;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [at, setAt] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [enrollmentDate, setEnrollmentDate] = useState('');
  const [admissionFee, setAdmissionFee] = useState('');
  const [allowDuplicate, setAllowDuplicate] = useState(false);

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ gradeOfferingId: application.gradeOfferingId }),
    queryFn: () =>
      sectionsApi.list({ page: 0, size: 100, gradeOfferingId: application.gradeOfferingId }),
    enabled: move === 'enrol',
  });

  const run = useMutation({
    mutationFn: async (): Promise<string> => {
      const id = application.id;
      switch (move) {
        case 'review':
          await admissionsApi.startReview(id);
          return 'Review started';
        case 'schedule':
          await admissionsApi.scheduleAssessment(id, new Date(at).toISOString(), optional(text));
          return 'Assessment scheduled';
        case 'record':
          await admissionsApi.recordAssessment(id, text.trim());
          return 'Assessment recorded';
        case 'offer':
          await admissionsApi.offer(id, optional(text));
          return 'Place offered';
        case 'accept':
          await admissionsApi.accept(id);
          return 'Acceptance recorded';
        case 'reject':
          await admissionsApi.reject(id, text.trim());
          return 'Application rejected';
        case 'withdraw':
          await admissionsApi.withdraw(id, optional(text));
          return 'Application withdrawn';
        case 'enrol': {
          const result = await admissionsApi.enrol(id, {
            sectionId: sectionId === '' ? undefined : sectionId,
            enrollmentDate: enrollmentDate === '' ? undefined : enrollmentDate,
            admissionFee: admissionFee.trim() === '' ? undefined : Number(admissionFee),
            allowDuplicate,
          });
          void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
          return `Enrolled as ${result.studentNumber} (enrolment ${result.enrollmentNumber})`;
        }
      }
    },
    onSuccess: (message) => {
      toast.success(message);
      onDone();
    },
    onError: (error) => handleApiError(error),
  });

  const title = `${MOVE_LABEL[move]} — ${application.applicationNumber}`;

  if (move === 'review' || move === 'accept') {
    return (
      <ConfirmDialog
        open
        title={title}
        message={
          move === 'review'
            ? `Move ${application.applicantName}'s application into review.`
            : `Record that the family of ${application.applicantName} has accepted the offered place.`
        }
        confirmLabel={MOVE_LABEL[move]}
        loading={run.isPending}
        onConfirm={() => run.mutate()}
        onCancel={onClose}
      />
    );
  }

  const destructive = move === 'reject' || move === 'withdraw';
  const incomplete =
    (move === 'schedule' && at === '') ||
    ((move === 'record' || move === 'reject') && text.trim() === '');

  return (
    <Modal
      open
      title={title}
      description={application.applicantName}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={run.isPending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            loading={run.isPending}
            disabled={incomplete}
            onClick={() => run.mutate()}
          >
            {MOVE_LABEL[move]}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {move === 'schedule' && (
          <>
            <TextField
              id="assessment-at"
              label="Date and time"
              type="datetime-local"
              required
              value={at}
              onChange={(event) => setAt(event.target.value)}
            />
            <TextField
              id="assessment-notes"
              label="Notes"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </>
        )}

        {move === 'record' && (
          <TextField
            id="assessment-result"
            label="Assessment notes"
            required
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        )}

        {move === 'offer' && (
          <TextField
            id="offer-note"
            label="Note"
            value={text}
            onChange={(event) => setText(event.target.value)}
            hint="Optional; kept with the decision"
          />
        )}

        {move === 'reject' && (
          <>
            <p className="text-sm text-muted">Rejection is final. A later attempt is a new application.</p>
            <TextField
              id="reject-reason"
              label="Reason"
              required
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </>
        )}

        {move === 'withdraw' && (
          <>
            <p className="text-sm text-muted">Withdrawal is final. A later attempt is a new application.</p>
            <TextField
              id="withdraw-reason"
              label="Reason"
              value={text}
              onChange={(event) => setText(event.target.value)}
              hint="Optional"
            />
          </>
        )}

        {move === 'enrol' && (
          <>
            <p className="text-sm text-muted">
              Creates the student record, the enrolment and any admission-fee invoice in one step.
            </p>
            <SelectField
              id="enrol-section"
              label="Section"
              value={sectionId}
              onChange={(event) => setSectionId(event.target.value)}
              hint="May be left for later placement."
            >
              <option value="">{sections.isPending ? 'Loading…' : 'Place later'}</option>
              {(sections.data?.data ?? []).map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </SelectField>
            <TextField
              id="enrol-date"
              label="Enrolment date"
              type="date"
              value={enrollmentDate}
              onChange={(event) => setEnrollmentDate(event.target.value)}
              hint="Defaults to today."
            />
            <TextField
              id="enrol-fee"
              label="Admission fee"
              type="number"
              min={0}
              step="0.01"
              value={admissionFee}
              onChange={(event) => setAdmissionFee(event.target.value)}
              hint="Leave blank for none."
            />
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={allowDuplicate}
                onChange={(event) => setAllowDuplicate(event.target.checked)}
              />
              <span>
                Enrol even if a student with the same name and birth date exists (twins, genuine
                namesakes).
              </span>
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

function AdmissionDocumentsPanel({ applicationId }: { applicationId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.ADMISSION_MANAGE);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('BIRTH_CERTIFICATE');
  const [deleting, setDeleting] = useState<AdmissionDocument | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const documents = useQuery({
    queryKey: queryKeys.admissions.documents(applicationId),
    queryFn: () => admissionsApi.documents(applicationId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.admissions.documents(applicationId) });

  const upload = useMutation({
    mutationFn: () => {
      if (file === null) {
        throw new Error('Choose a file first');
      }
      return admissionsApi.uploadDocument(applicationId, file, documentType);
    },
    onSuccess: () => {
      toast.success('Document attached');
      setUploadOpen(false);
      setFile(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const remove = useMutation({
    mutationFn: (documentId: string) => admissionsApi.deleteDocument(applicationId, documentId),
    onSuccess: () => {
      toast.success('Document deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setDeleting(null);
    },
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Documents"
          description="Papers received with the application."
          action={
            manages ? (
              <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
                Upload
              </Button>
            ) : undefined
          }
        />

        {documents.isPending ? (
          <LoadingState label="Loading documents" />
        ) : documents.error !== null ? (
          <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
        ) : documents.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">No documents attached yet.</p>
        ) : (
          <ul className="divide-y divide-line px-5">
            {documents.data.map((document) => (
              <li key={document.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{humanise(document.documentType)}</p>
                  <p className="mt-0.5 truncate text-xs text-subtle">
                    {document.fileName}
                    {document.fileSize !== undefined && ` · ${formatSize(document.fileSize)}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={downloading === document.id}
                    onClick={() => {
                      setDownloading(document.id);
                      void admissionsApi
                        .downloadDocument(applicationId, document.id, document.fileName)
                        .catch((error: unknown) => handleApiError(error))
                        .finally(() => setDownloading(null));
                    }}
                  >
                    Download
                  </Button>
                  {manages && (
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(document)}>
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Attach a document"
        footer={
          <>
            <Button variant="secondary" onClick={() => setUploadOpen(false)} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={() => upload.mutate()} loading={upload.isPending} disabled={file === null}>
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="admission-document-file" className="mb-1.5 block text-xs font-medium text-muted">
              File<span className="ml-0.5 text-danger">*</span>
            </label>
            <input
              id="admission-document-file"
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
            />
          </div>
          <SelectField
            id="admission-document-type"
            label="Document type"
            required
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value)}
          >
            {DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title="Delete this document?"
        message={`${deleting?.fileName ?? ''} will be removed from storage. This cannot be undone.`}
        confirmLabel="Delete"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id);
          }
        }}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-subtle">{label}</dt>
      <dd className="mt-1 text-sm text-ink">{value}</dd>
    </div>
  );
}
